import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const StyledModal = styled(Modal)`
  .modal-content {
    background-color: #ffffff;
    box-shadow: inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border-radius: ${(p) => p.theme.radius.md};
    border: none;
  }
  .modal-header {
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    padding: calc(${(p) => p.theme.spacing.md} + 4px)
      ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.md};
  }
  .modal-body {
    padding: ${(p) => p.theme.spacing.md};
    overflow: visible;
  }
  .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  }
`;

const ModalTitle = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeLg};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => p.theme.color.primary};
  line-height: 1.2;
`;

const LeadText = styled.p`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};
  line-height: 1.5;
  margin: 0 0 ${(p) => p.theme.spacing.md} 0;
`;

const InfoCard = styled.div`
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing.sm};
`;

const PartRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .form-check-label {
    font-family: ${(p) => p.theme.font.family};
    font-size: ${(p) => p.theme.font.sizeMd};
    font-weight: ${(p) => p.theme.font.weightSemibold};
    color: ${(p) => p.theme.color.primary};
  }
`;

const PartHint = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.textMuted};
  padding-left: 1.5rem;
`;

export const ALL_CLONE_PARTS = {
  specsheet: true,
  note: true,
  thumbCuts: true,
};

/**
 * What the clone button copies, asked instead of assumed.
 *
 * Cloning used to take the whole sheet including the notes -- which are about
 * one ball on one day -- and every thumb cut. The three parts come apart
 * cleanly, so the driller says which ones travel.
 */
const CloneSpecsheetModal = ({ show, onHide, onConfirm, thumbCutCount = 0 }) => {
  const { t } = useTranslation();
  const [parts, setParts] = useState(ALL_CLONE_PARTS);

  // Every clone starts from all three again; a box left unticked last time is
  // not a preference, it was a decision about that sheet.
  useEffect(() => {
    if (show) setParts(ALL_CLONE_PARTS);
  }, [show]);

  const toggle = (part) => () =>
    setParts((prev) => ({ ...prev, [part]: !prev[part] }));

  return (
    <StyledModal show={show} onHide={onHide} size="md" centered>
      <Modal.Header closeButton>
        <ModalTitle>{t("message.confirm.cloneSpecsheetTitle")}</ModalTitle>
      </Modal.Header>
      <Modal.Body>
        <LeadText>{t("message.confirm.cloneSpecsheetBody")}</LeadText>
        <InfoCard>
          <PartRow>
            <Form.Check
              type="checkbox"
              id="clone-part-specsheet"
              label={t("message.confirm.clonePartSpecsheet")}
              checked={parts.specsheet}
              onChange={toggle("specsheet")}
            />
            <PartHint>{t("message.confirm.clonePartSpecsheetHint")}</PartHint>
          </PartRow>
          <PartRow>
            <Form.Check
              type="checkbox"
              id="clone-part-note"
              label={t("message.confirm.clonePartNote")}
              checked={parts.note}
              onChange={toggle("note")}
            />
          </PartRow>
          <PartRow>
            <Form.Check
              type="checkbox"
              id="clone-part-thumb-cuts"
              label={`${t("message.confirm.clonePartThumbCuts")} (${thumbCutCount})`}
              checked={parts.thumbCuts}
              disabled={thumbCutCount === 0}
              onChange={toggle("thumbCuts")}
            />
          </PartRow>
        </InfoCard>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          {t("message.confirm.cancel")}
        </Button>
        <Button variant="primary" onClick={() => onConfirm(parts)}>
          {t("form.label.clone")}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default CloneSpecsheetModal;
